/**
 * <s-product-slider> — product gallery slider with previous/next arrows.
 *
 * Required markup:
 *   [data-slider-track]   — horizontally scrolling container, one slide per child.
 *   [data-slider-prev]    — previous button (optional).
 *   [data-slider-next]    — next button (optional).
 *   [data-slider-current] — element showing the current slide number (optional).
 */
class SProductSlider extends HTMLElement {
  #ac;
  #raf = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.prev = this.querySelector('[data-slider-prev]');
    this.next = this.querySelector('[data-slider-next]');
    this.current = this.querySelector('[data-slider-current]');
    if (!this.track) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.prev?.addEventListener('click', () => this.go(this.index - 1), opts);
    this.next?.addEventListener('click', () => this.go(this.index + 1), opts);
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => this.#update());
    }, { ...opts, passive: true });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
      if (event.key === 'ArrowRight') this.go(this.index + 1);
    }, opts);

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
  }

  get slides() {
    return Array.from(this.track.children);
  }

  get index() {
    const width = this.track.clientWidth || 1;
    return Math.round(Math.abs(this.track.scrollLeft) / width);
  }

  go(target) {
    const count = this.slides.length;
    if (!count) return;
    const index = Math.max(0, Math.min(count - 1, target));
    const dir = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: dir * index * this.track.clientWidth });
  }

  #update() {
    const count = this.slides.length;
    const index = this.index;
    if (this.prev) this.prev.disabled = index <= 0;
    if (this.next) this.next.disabled = index >= count - 1;
    if (this.current) this.current.textContent = String(Math.min(index + 1, count));
  }
}

if (!customElements.get('s-product-slider')) {
  customElements.define('s-product-slider', SProductSlider);
}
